import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import SuritLogo from "../components/brand/SuritLogo";
import { api } from "../lib/api";

// RC.1 -- «¿Olvidaste tu contraseña?». Pide un enlace para crear una contraseña nueva; el enlace
// lleva a /acceso (la pantalla de TEN.4), que es donde se fija.
//
// 🔴 El mensaje tras enviar es el MISMO exista o no la cuenta: el backend contesta igual en los
//    dos casos y esta pantalla no distingue nada (no se enumeran cuentas).
// 🔴 El correo viaja en el CUERPO del POST; desde el login llega por `state`, nunca por la URL.
// 🔴 La vigencia del enlace no se escribe aquí: la dice el correo (PASSWORD_TEMPORAL_HORAS).

export default function RecuperarAcceso() {
  const loc = useLocation();
  const [email, setEmail] = useState(loc.state?.email || "");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [err, setErr] = useState("");

  const enviar = async (e) => {
    e.preventDefault();
    setErr("");
    setEnviando(true);
    try {
      await api.post("/auth/acceso/solicitar", { email: email.trim() });
      setEnviado(true);
    } catch (e2) {
      const r = e2.response;
      if (r?.status === 422) setErr("Revisa que el correo esté bien escrito.");
      else if (r?.status === 429 && typeof r.data?.detail === "string") setErr(r.data.detail);
      else setErr("No pudimos enviar tu solicitud. Intenta de nuevo en un momento.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-card p-8">
        <Link to="/" className="flex items-center w-fit"><SuritLogo size={42} tone="claro" rotulo label="Surit Clínicas" /></Link>

        {enviado ? (
          <div className="mt-6 space-y-4" data-testid="recuperar-enviado">
            <h1 className="font-display text-2xl font-bold">Revisa tu correo</h1>
            <p className="text-sm text-slate-700">
              Si ese correo tiene una cuenta en Surit Clínicas, te enviamos un enlace para crear una
              contraseña nueva. Si no lo ves, revisa la carpeta de correo no deseado.
            </p>
            <Link to="/login" className="inline-block text-sm font-medium text-blue-700 underline">Volver a iniciar sesión</Link>
          </div>
        ) : (
          <form onSubmit={enviar} className="mt-6 space-y-4" data-testid="recuperar-form">
            <h1 className="font-display text-2xl font-bold">Recupera tu acceso</h1>
            <p className="text-sm text-slate-600">
              Escribe el correo con el que entras al panel y te enviaremos un enlace para crear una
              contraseña nueva.
            </p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Correo electrónico</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="recuperar-email" required />
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="recuperar-error">{err}</div>}
            <button type="submit" disabled={enviando}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-medium py-3 rounded-xl hover:bg-blue-700 disabled:opacity-60"
              data-testid="recuperar-submit">
              {enviando ? (<><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</>) : "Enviar enlace"}
            </button>
            <Link to="/login" className="inline-block text-sm text-slate-600 hover:text-slate-800 underline">Volver a iniciar sesión</Link>
          </form>
        )}
      </div>
    </div>
  );
}
